import axios from 'axios';
import { toast } from 'react-hot-toast';

// Configuração base do axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requests
api.interceptors.request.use(
  (config) => {
    // Adicionar token se existir (usuário ou colaborador)
    const userToken = localStorage.getItem('token');
    const colaboradorToken = localStorage.getItem('colaboradorToken');
    
    console.log('🔍 API Request:', config.url);
    console.log('🔍 User Token:', userToken ? 'Presente' : 'Ausente');
    console.log('🔍 Colaborador Token:', colaboradorToken ? 'Presente' : 'Ausente');
    
    if (userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
    } else if (colaboradorToken) {
      config.headers.Authorization = `Bearer ${colaboradorToken}`;
    }
    
    // Adicionar device ID para mobile (se não estiver no body)
    if (config.data && !config.data.deviceId) {
      const deviceId = localStorage.getItem('deviceId');
      if (deviceId) {
        config.data.deviceId = deviceId;
      }
    }
    
    // Não definir Content-Type para uploads (deixar o multer definir)
    if (config.headers['Content-Type'] === 'multipart/form-data') {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para responses
api.interceptors.response.use(
  (response) => {
    // Log dos cabeçalhos para debug
    console.log('📡 Response headers:', response.headers);
    console.log('📡 Response status:', response.status);
    console.log('📡 Response data:', response.data);
    
    return response;
  },
  async (error) => {
    console.error('❌ API Error:', error);
    console.error('❌ Error response:', error.response);
    console.error('❌ Error headers:', error.response?.headers);
    console.error('❌ Error details:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    const originalRequest = error.config;
    
    // Se for erro 401 (não autorizado) e não for uma tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('❌ Token inválido, limpando localStorage');
      originalRequest._retry = true;
      
      try {
        // Verificar se é token de usuário ou colaborador
        const userToken = localStorage.getItem('token');
        const colaboradorToken = localStorage.getItem('colaboradorToken');
        
        console.log('🔍 Tentando refresh token...');
        console.log('🔍 User Token:', userToken ? 'Presente' : 'Ausente');
        console.log('🔍 Colaborador Token:', colaboradorToken ? 'Presente' : 'Ausente');
        
        if (userToken) {
          // Tentar renovar token de usuário
          const refreshToken = localStorage.getItem('refreshToken');
          console.log('🔍 Refresh Token:', refreshToken ? 'Presente' : 'Ausente');
          if (refreshToken) {
            const response = await api.post('/auth/refresh', { refreshToken });
            console.log('🔍 Refresh response:', response.data);
            
            if (response.data.success) {
              const { token } = response.data;
              localStorage.setItem('token', token);
              console.log('✅ Token renovado com sucesso');
              
              // Atualizar header da requisição original
              originalRequest.headers.Authorization = `Bearer ${token}`;
              
              // Retry da requisição original
              return api(originalRequest);
            }
          }
          
          // Se não conseguir renovar, redirecionar para login de usuário
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          
        } else if (colaboradorToken) {
          // Para colaboradores, não há refresh token, então redirecionar direto
          localStorage.removeItem('colaboradorToken');
          window.location.href = '/login-colaborador';
        }
      } catch (refreshError) {
        console.error('Erro ao renovar token:', refreshError);
        
        // Limpar tokens e redirecionar
        localStorage.removeItem('token');
        localStorage.removeItem('colaboradorToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
    
    // Tratar outros erros
    if (error.response?.status >= 500) {
      toast.error('Erro interno do servidor. Tente novamente mais tarde.');
    } else if (error.response?.status === 403) {
      toast.error('Acesso negado. Você não tem permissão para esta ação.');
    } else if (error.response?.status === 404) {
      toast.error('Recurso não encontrado.');
    } else if (error.response?.status === 422) {
      // Erro de validação
      const errors = error.response.data.errors;
      if (errors && errors.length > 0) {
        errors.forEach(err => {
          toast.error(err.msg || 'Erro de validação');
        });
      }
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Tempo limite da requisição excedido. Verifique sua conexão.');
    } else if (!error.response) {
      toast.error('Erro de conexão. Verifique sua internet.');
    }
    
    return Promise.reject(error);
  }
);

// Funções auxiliares para diferentes tipos de requisições
export const apiService = {
  // GET
  get: (url, config = {}) => api.get(url, config),
  
  // POST
  post: (url, data = {}, config = {}) => api.post(url, data, config),
  
  // PUT
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  
  // DELETE
  delete: (url, config = {}) => api.delete(url, config),
  
  // PATCH
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  
  // Upload de arquivo
  upload: (url, formData, config = {}) => {
    const uploadConfig = {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.post(url, formData, uploadConfig);
  },
  
  // Download de arquivo
  download: (url, config = {}) => {
    const downloadConfig = {
      ...config,
      responseType: 'blob',
    };
    return api.get(url, downloadConfig);
  }
};

// Funções específicas para cada módulo
export const pontoService = {
  // Registrar ponto
  registrar: (dados) => apiService.post('/pontos/registrar', dados),
  
  // Buscar meus pontos
  meusPontos: (filtros = {}) => apiService.get('/pontos/meus-pontos', { params: filtros }),
  
  // Buscar pontos de colaborador
  pontosColaborador: (id, filtros = {}) => apiService.get(`/pontos/colaborador/${id}`, { params: filtros }),
  
  // Buscar pontos do usuário logado
  pontosUsuario: (id, filtros = {}) => apiService.get(`/pontos/usuario/${id}`, { params: filtros }),
  
  // Aprovar ponto
  aprovar: (id, dados = {}) => apiService.put(`/pontos/${id}/aprovar`, dados),
  
  // Rejeitar ponto
  rejeitar: (id, dados) => apiService.put(`/pontos/${id}/rejeitar`, dados),
  
  // Ajustar ponto
  ajustar: (id, dados) => apiService.put(`/pontos/${id}/ajustar`, dados),
  
  // Folha de ponto
  folha: (dataInicio, dataFim, colaboradorId) => apiService.get('/pontos/folha', { params: { dataInicio, dataFim, ...(colaboradorId ? { colaboradorId } : {}) } }),

  // Relatório de pontos
  relatorio: (filtros) => apiService.get('/pontos/relatorio', { params: filtros })
};

export const veiculoService = {
  // Listar veículos
  listar: (filtros = {}) => apiService.get('/veiculos', { params: filtros }),
  
  // Buscar veículo por ID
  buscar: (id) => apiService.get(`/veiculos/${id}`),
  
  // Criar veículo
  criar: (dados) => apiService.post('/veiculos', dados),
  
  // Atualizar veículo
  atualizar: (id, dados) => apiService.put(`/veiculos/${id}`, dados),
  
  // Deletar veículo
  deletar: (id) => apiService.delete(`/veiculos/${id}`),
  
  // Atualizar hodômetro
  atualizarHodometro: (id, valor) => apiService.put(`/veiculos/${id}/hodometro`, { valor }),
  
  // Buscar histórico de pontos do veículo
  historicoPontos: (id, filtros = {}) => apiService.get(`/veiculos/${id}/historico-pontos`, { params: filtros }),
  
  // Upload do CRLV
  uploadCRLV: (id, file) => {
    const formData = new FormData();
    formData.append('crlv', file);
    
    return apiService.post(`/veiculos/${id}/crlv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Remover CRLV
  removerCRLV: (id) => apiService.delete(`/veiculos/${id}/crlv`),
  
  // Download do CRLV
  downloadCRLV: (id) => apiService.get(`/veiculos/${id}/crlv`, { responseType: 'blob' }),
  // Consultar dados do veiculo pela placa
  consultarPlaca: (placa) => apiService.get(`/veiculos/consulta-placa/${placa}`),
};

export const manutencaoService = {
  // Listar manutenções
  listar: (filtros = {}) => apiService.get('/manutencoes', { params: filtros }),
  
  // Buscar manutenção por ID
  buscar: (id) => apiService.get(`/manutencoes/${id}`),
  
  // Criar manutenção
  criar: (dados) => apiService.post('/manutencoes', dados),
  
  // Atualizar manutenção
  atualizar: (id, dados) => apiService.put(`/manutencoes/${id}`, dados),
  
  // Finalizar manutenção
  finalizar: (id) => apiService.post(`/manutencoes/${id}/finalizar`),
  
  // Cancelar manutenção
  cancelar: (id) => apiService.post(`/manutencoes/${id}/cancelar`),
  
  // Buscar manutenções de um veículo
  porVeiculo: (veiculoId, filtros = {}) => apiService.get(`/manutencoes/veiculo/${veiculoId}`, { params: filtros }),
  
  // Buscar veículos disponíveis para substituição
  veiculosDisponiveis: () => apiService.get('/manutencoes/veiculos/disponiveis')
};

// Estoque / Inventário (Fase 1)
export const estoqueService = {
  // Itens (cadastro)
  listarItens: (filtros = {}) => apiService.get('/estoque/itens', { params: filtros }),
  criarItem: (dados) => apiService.post('/estoque/itens', dados),
  atualizarItem: (id, dados) => apiService.put(`/estoque/itens/${id}`, dados),

  // Saldo disponível por item
  listarItensDisponiveis: (filtros = {}) => apiService.get('/estoque/itens-disponiveis', { params: filtros }),

  // Lotes do item (com validade e saldo do lote)
  listarLotes: (itemId, filtros = {}) =>
    apiService.get(`/estoque/lotes/${itemId}`, { params: filtros }),

  // Entradas (cria lote com saldo inicial)
  criarEntrada: (dados) => apiService.post('/estoque/entradas', dados),
};

export const ordemServicoService = {
  // Listar ordens de serviço
  listar: (filtros = {}) => apiService.get('/ordens-servico', { params: filtros }),
  
  // Buscar OS por ID
  buscar: (id) => apiService.get(`/ordens-servico/${id}`),
  
  // Criar OS
  criar: (dados) => apiService.post('/ordens-servico', dados),
  
  // Atualizar OS
  atualizar: (id, dados) => apiService.put(`/ordens-servico/${id}`, dados),
  
  // Deletar OS
  deletar: (id) => apiService.delete(`/ordens-servico/${id}`),
  
  // Iniciar OS
  iniciar: (id) => apiService.put(`/ordens-servico/${id}/iniciar`),
  
  // Concluir OS
  concluir: (id) => apiService.put(`/ordens-servico/${id}/concluir`),
  
  // Cancelar OS
  cancelar: (id, motivo) => apiService.put(`/ordens-servico/${id}/cancelar`, { motivo }),
  
  // Importar dados de OS a partir de PDF
  importarPdf: (file) => {
    const formData = new FormData();
    formData.append('arquivo', file);
    return apiService.post('/ordens-servico/importar-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Importar multiplas OS de PDFs e criar automaticamente
  importarPdfs: (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('arquivos', f));
    return apiService.post('/ordens-servico/importar-pdfs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// OS Operacional de Transporte (vehicle + driver, checklist, passageiros)
export const serviceOrderOperacionalService = {
  listar: (filtros = {}) => apiService.get('/service-orders', { params: filtros }),
  buscar: (id) => apiService.get(`/service-orders/${id}`),
  criar: (dados) => apiService.post('/service-orders', dados),
  atualizar: (id, dados) => apiService.put(`/service-orders/${id}`, dados),
  atualizarChecklist: (id, checklist) => apiService.patch(`/service-orders/${id}/checklist`, { checklist }),
  deletar: (id) => apiService.delete(`/service-orders/${id}`),
  pdf: (id) => apiService.get(`/service-orders/${id}/pdf`, { responseType: 'blob' })
};

// Notificações in-app (ex.: OS designada ao motorista)
export const notificacoesService = {
  listar: () => apiService.get('/notificacoes'),
  marcarLida: (id) => apiService.patch(`/notificacoes/${id}/ler`),
  marcarTodasLidas: () => apiService.patch('/notificacoes/ler-todas')
};

export const colaboradorService = {
  // Listar colaboradores
  listar: (filtros = {}) => apiService.get('/colaboradores', { params: filtros }),
  
  // Buscar colaborador por ID
  buscar: (id) => apiService.get(`/colaboradores/${id}`),
  
  // Criar colaborador
  criar: (dados) => apiService.post('/colaboradores', dados),
  
  // Atualizar colaborador
  atualizar: (id, dados) => apiService.put(`/colaboradores/${id}`, dados),
  
  // Deletar colaborador
  deletar: (id) => apiService.delete(`/colaboradores/${id}`),
  
  // Buscar por matrícula
  buscarPorMatricula: (matricula) => apiService.get(`/colaboradores/matricula/${matricula}`),
  
  // Ativar/desativar colaborador
  toggleStatus: (id) => apiService.patch(`/colaboradores/${id}/toggle-status`),
  
  // Upload de foto
  uploadFoto: (id, formData) => apiService.upload(`/colaboradores/${id}/foto`, formData),
  
  // Gerar ficha cadastral
  gerarFichaCadastral: (id) => apiService.get(`/colaboradores/${id}/ficha-cadastral`, {
    responseType: 'blob'
  })
};

export const usuarioService = {
  // Listar usuários
  listar: (filtros = {}) => apiService.get('/usuarios', { params: filtros }),
  
  // Buscar usuário por ID
  buscar: (id) => apiService.get(`/usuarios/${id}`),
  
  // Criar usuário
  criar: (dados) => apiService.post('/usuarios', dados),
  
  // Atualizar usuário
  atualizar: (id, dados) => apiService.put(`/usuarios/${id}`, dados),
  
  // Deletar usuário
  deletar: (id) => apiService.delete(`/usuarios/${id}`),
  
  // Ativar/desativar usuário
  toggleStatus: (id) => apiService.put(`/usuarios/${id}/toggle-status`)
};

export const relatorioService = {
  // Folha de ponto
  folha: (dataInicio, dataFim, colaboradorId) => apiService.get('/pontos/folha', { params: { dataInicio, dataFim, ...(colaboradorId ? { colaboradorId } : {}) } }),

  // Relatório de pontos
  pontos: (filtros) => apiService.get('/relatorios/pontos', { params: filtros }),
  
  // Relatório de frota
  frota: (filtros) => apiService.get('/relatorios/frota', { params: filtros }),
  
  // Relatório financeiro
  financeiro: (filtros) => apiService.get('/relatorios/financeiro', { params: filtros }),
  
  // Relatório de RH
  rh: (filtros) => apiService.get('/relatorios/rh', { params: filtros }),
  
  // Exportar relatório
  exportar: (tipo, filtros, formato = 'pdf') => 
    apiService.download(`/relatorios/${tipo}/exportar`, { 
      params: { ...filtros, formato } 
    })
};

export const atestadoService = {
  // Listar atestados
  listar: (filtros = {}) => apiService.get('/atestados', { params: filtros }),
  
  // Buscar atestado por ID
  buscar: (id) => apiService.get(`/atestados/${id}`),
  
  // Criar atestado
  criar: (dados) => apiService.post('/atestados', dados),
  
  // Atualizar atestado
  atualizar: (id, dados) => apiService.put(`/atestados/${id}`, dados),
  
  // Deletar atestado
  deletar: (id) => apiService.delete(`/atestados/${id}`),
  
  // Aprovar atestado
  aprovar: (id, dados) => apiService.post(`/atestados/${id}/aprovar`, dados),
  
  // Recusar atestado
  recusar: (id, dados) => apiService.post(`/atestados/${id}/recusar`, dados),
  
  // Reabrir atestado
  reabrir: (id) => apiService.post(`/atestados/${id}/reabrir`),
  
  // Buscar atestados por colaborador
  buscarPorColaborador: (colaboradorId) => apiService.get(`/atestados/colaborador/${colaboradorId}`),
  
  // Estatísticas gerais
  estatisticas: () => apiService.get('/atestados/stats/geral'),
  
  // ===== MÉTODOS PARA MOTORISTAS =====
  // Buscar meus atestados
  meusAtestados: () => apiService.get('/atestados/meus'),
  
  // Upload de atestado
  upload: (formData) => apiService.post('/atestados/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
};

// Serviço para Rotas
export const rotaService = {
  // Listar rotas
  listar: (params = {}) => apiService.get('/rotas', { params }),
  
  // Buscar rota por ID
  buscarPorId: (id) => apiService.get(`/rotas/${id}`),
  
  // Criar nova rota
  criar: (dados) => apiService.post('/rotas', dados),
  
  // Atualizar rota
  atualizar: (id, dados) => apiService.put(`/rotas/${id}`, dados),
  
  // Deletar rota
  deletar: (id) => apiService.delete(`/rotas/${id}`)
};

// Serviço para Chamada de Alunos
export const chamadaAlunosService = {
  // Listar chamadas
  listar: (params = {}) => apiService.get('/chamada-alunos', { params }),
  
  // Buscar chamada por ID
  buscarPorId: (id) => apiService.get(`/chamada-alunos/${id}`),
  
  // Criar nova chamada
  criar: (dados) => apiService.post('/chamada-alunos', dados),
  
  // Atualizar chamada
  atualizar: (id, dados) => apiService.put(`/chamada-alunos/${id}`, dados),
  
  // Atualizar status de um aluno
  atualizarStatusAluno: (chamadaId, alunoId, status, observacoes = '') => 
    apiService.put(`/chamada-alunos/${chamadaId}/alunos/${alunoId}`, { status, observacoes }),
  
  // Finalizar chamada
  finalizar: (id) => apiService.put(`/chamada-alunos/${id}/finalizar`),
  
  // Deletar chamada
  deletar: (id) => apiService.delete(`/chamada-alunos/${id}`),
  
  // Relatório de estatísticas
  relatorio: (params = {}) => apiService.get('/chamada-alunos/relatorio/estatisticas', { params })
};

// Serviço para Alunos
export const alunoService = {
  // Listar alunos
  listar: (params = {}) => apiService.get('/alunos', { params }),
  
  // Buscar aluno por ID
  buscarPorId: (id) => apiService.get(`/alunos/${id}`),
  
  // Criar novo aluno
  criar: (dados) => apiService.post('/alunos', dados),
  
  // Atualizar aluno
  atualizar: (id, dados) => apiService.put(`/alunos/${id}`, dados),
  
  // Deletar aluno
  deletar: (id) => apiService.delete(`/alunos/${id}`)
};

// Serviço para Abastecimentos Financeiros
export const abastecimentoFinanceiroService = {
  // Listar abastecimentos financeiros
  listar: (filtros = {}) => apiService.get('/abastecimentos-financeiros', { params: filtros }),
  
  // Buscar abastecimento por ID
  buscar: (id) => apiService.get(`/abastecimentos-financeiros/${id}`),
  
  // Buscar estatísticas de um veículo
  estatisticas: (veiculoId, filtros = {}) => apiService.get(`/abastecimentos-financeiros/estatisticas/${veiculoId}`, { params: filtros }),
  
  // Criar abastecimento
  criar: (dados) => {
    const formData = new FormData();
    
    // Adicionar campos do formulário
    Object.keys(dados).forEach(key => {
      if (key === 'nfeArquivo' && dados[key]) {
        formData.append('nfeArquivo', dados[key]);
      } else if (key === 'posto' && typeof dados[key] === 'object') {
        // Adicionar campos do posto individualmente
        Object.keys(dados[key]).forEach(postoKey => {
          if (typeof dados[key][postoKey] === 'object' && dados[key][postoKey] !== null) {
            // Para endereco
            Object.keys(dados[key][postoKey]).forEach(enderecoKey => {
              formData.append(`posto.${postoKey}.${enderecoKey}`, dados[key][postoKey][enderecoKey]);
            });
          } else {
            formData.append(`posto.${postoKey}`, dados[key][postoKey]);
          }
        });
      } else if (key === 'nfe' && typeof dados[key] === 'object') {
        // Adicionar campos da NF-e individualmente
        Object.keys(dados[key]).forEach(nfeKey => {
          if (nfeKey !== 'arquivo') { // arquivo é tratado separadamente
            formData.append(`nfe.${nfeKey}`, dados[key][nfeKey]);
          }
        });
      } else if (dados[key] !== null && dados[key] !== undefined) {
        formData.append(key, dados[key]);
      }
    });
    
    return api.post('/abastecimentos-financeiros', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Atualizar abastecimento
  atualizar: (id, dados) => {
    const formData = new FormData();
    
    // Adicionar campos do formulário
    Object.keys(dados).forEach(key => {
      if (key === 'nfeArquivo' && dados[key]) {
        formData.append('nfeArquivo', dados[key]);
      } else if (key === 'posto' && typeof dados[key] === 'object') {
        // Adicionar campos do posto individualmente
        Object.keys(dados[key]).forEach(postoKey => {
          if (typeof dados[key][postoKey] === 'object' && dados[key][postoKey] !== null) {
            // Para endereco
            Object.keys(dados[key][postoKey]).forEach(enderecoKey => {
              formData.append(`posto.${postoKey}.${enderecoKey}`, dados[key][postoKey][enderecoKey]);
            });
          } else {
            formData.append(`posto.${postoKey}`, dados[key][postoKey]);
          }
        });
      } else if (key === 'nfe' && typeof dados[key] === 'object') {
        // Adicionar campos da NF-e individualmente
        Object.keys(dados[key]).forEach(nfeKey => {
          if (nfeKey !== 'arquivo') { // arquivo é tratado separadamente
            formData.append(`nfe.${nfeKey}`, dados[key][nfeKey]);
          }
        });
      } else if (dados[key] !== null && dados[key] !== undefined) {
        formData.append(key, dados[key]);
      }
    });
    
    return api.put(`/abastecimentos-financeiros/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Aprovar abastecimento
  aprovar: (id) => apiService.post(`/abastecimentos-financeiros/${id}/aprovar`),
  
  // Conferir abastecimento
  conferir: (id) => apiService.post(`/abastecimentos-financeiros/${id}/conferir`),
  
  // Rejeitar abastecimento
  rejeitar: (id, motivoRejeicao) => apiService.post(`/abastecimentos-financeiros/${id}/rejeitar`, { motivoRejeicao }),
  
  // Excluir abastecimento
  excluir: (id) => apiService.delete(`/abastecimentos-financeiros/${id}`),
  
  // Baixar NF-e
  baixarNfe: (filename) => apiService.get(`/abastecimentos-financeiros/nfe/${filename}`, {
    responseType: 'blob'
  })
};

// Serviço para Abastecimentos
export const abastecimentoService = {
  // Listar abastecimentos
  listar: (filtros = {}) => apiService.get('/abastecimentos', { params: filtros }),
  
  // Buscar abastecimento por ID
  buscar: (id) => apiService.get(`/abastecimentos/${id}`),
  
  // Criar abastecimento
  criar: (dados) => {
    const formData = new FormData();
    
    // Adicionar campos do formulário
    Object.keys(dados).forEach(key => {
      if (key === 'comprovante' && dados[key]) {
        formData.append('comprovante', dados[key]);
      } else if (key === 'posto' && typeof dados[key] === 'object') {
        // Adicionar campos do posto individualmente
        Object.keys(dados[key]).forEach(postoKey => {
          if (typeof dados[key][postoKey] === 'object' && dados[key][postoKey] !== null) {
            // Para endereco
            Object.keys(dados[key][postoKey]).forEach(enderecoKey => {
              formData.append(`posto.${postoKey}.${enderecoKey}`, dados[key][postoKey][enderecoKey]);
            });
          } else {
            formData.append(`posto.${postoKey}`, dados[key][postoKey]);
          }
        });
      } else if (dados[key] !== null && dados[key] !== undefined) {
        formData.append(key, dados[key]);
      }
    });
    
    return api.post('/abastecimentos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Atualizar abastecimento
  atualizar: (id, dados) => {
    const formData = new FormData();
    
    // Adicionar campos do formulário
    Object.keys(dados).forEach(key => {
      if (key === 'comprovante' && dados[key]) {
        formData.append('comprovante', dados[key]);
      } else if (key === 'posto' && typeof dados[key] === 'object') {
        // Adicionar campos do posto individualmente
        Object.keys(dados[key]).forEach(postoKey => {
          if (typeof dados[key][postoKey] === 'object' && dados[key][postoKey] !== null) {
            // Para endereco
            Object.keys(dados[key][postoKey]).forEach(enderecoKey => {
              formData.append(`posto.${postoKey}.${enderecoKey}`, dados[key][postoKey][enderecoKey]);
            });
          } else {
            formData.append(`posto.${postoKey}`, dados[key][postoKey]);
          }
        });
      } else if (dados[key] !== null && dados[key] !== undefined) {
        formData.append(key, dados[key]);
      }
    });
    
    return api.put(`/abastecimentos/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Aprovar abastecimento
  aprovar: (id) => apiService.post(`/abastecimentos/${id}/aprovar`),
  
  // Rejeitar abastecimento
  rejeitar: (id, motivoRejeicao) => apiService.post(`/abastecimentos/${id}/rejeitar`, { motivoRejeicao }),
  
  // Excluir abastecimento
  excluir: (id) => apiService.delete(`/abastecimentos/${id}`),
  
  // Baixar comprovante
  baixarComprovante: (filename) => apiService.get(`/abastecimentos/comprovante/${filename}`, {
    responseType: 'blob'
  })
};


export default api;

